import { GameManager } from "@/app/lib/game-state/game-manager";
import UI from "@/app/lib/ui/ui";
import { isPath, Path } from "@/app/lib/routing/path";
import { GameMode } from "@/app/lib/game-state/game-types";
import { isLevelName, LevelName } from "@/app/lib/levels/full-story";
import * as Util from "@/app/lib/system/util";

export class InternalRouter {
    private gameManager: GameManager;
    private ui: UI;

    constructor(gameManager: GameManager, ui: UI) {
        this.gameManager = gameManager;
        this.ui = ui;
    }

    goPath(path: Path, addState = true): void {
        Util.devLog('Internally routing to ' + path);

        if (path.startsWith('/story')) {
            this.gameManager.setActiveMode('story');
            // handle level swaps
            const levelName = this.getLevelNameFromStoryPath(path);
            if (levelName) {
                this.gameManager.levels.move.byName(levelName);
            }
            // if we got to just /story, route to the current level
            if (path === '/story') {
                const currentLevelData = this.gameManager.levels.getCurrentData();
                const levelName = currentLevelData?.levelName;
                const storyPath = levelName
                    ? this.getStoryPathFromLevelName(levelName)
                    : null;
                if (storyPath) {
                    path = storyPath;
                }
            }
        }
        else {
            this.gameManager.setActiveMode('pyramid');
        }

        this.ui.setPath(path);

        // we push state to history by default, but can choose not to
        // helpful for back/forward pop state events
        if (addState) {
            window.history.pushState({ path }, '', path)
        } else {
            window.history.replaceState({ path }, '', path);
        }
    }

    goMode(mode: GameMode, addState = true): void {
        if (mode === 'pyramid') {
            this.goPath('/', addState);
            return;
        }

        if (mode === 'story') {
            this.goPath('/story', addState);
            return;
        }
    }

    toggleMode(): void {
        if (this.gameManager.activeMode === 'story') {
            this.goPath('/');
        }
        if (this.gameManager.activeMode === 'pyramid') {
            this.goPath('/story');
        }
    }

    goLevelName(levelName: LevelName, addState = true) {
        // we must kebabize for urls
        const kebabLevelName = levelName.replaceAll(' ', '-');
        const path = '/story/' + kebabLevelName;
        if (!isPath(path)) {
            return;
        }
        // and in the path
        this.goPath(path, addState);
    }

    goLevelNumber(levelNumber: number, addState = true) {
        // get the level name by number
        const levelData = this.gameManager.levels.fullStory.getLevelByNumber(levelNumber);
        if (!levelData) {
            return;
        }
        this.goLevelName(levelData.levelName, addState);
    }

    goForwardLevel(addState = true) {
        // get current level number
        const currentLevelNumber: number = this.gameManager.levels.current;
        this.goLevelNumber(currentLevelNumber + 1, addState);
    }

    goBackLevel(addState = true) {
        // get current level number
        const currentLevelNumber: number = this.gameManager.levels.current;
        this.goLevelNumber(currentLevelNumber - 1, addState);
    }

    getLevelNameFromStoryPath(path: Path) : LevelName | null {
        const maybeKebabLevelName = path.slice('/story/'.length);
        const maybeLevelName = maybeKebabLevelName.replaceAll('-', ' ');
        if (isLevelName(maybeLevelName)) {
            return maybeLevelName;
        }
        return null;
    }

    getStoryPathFromLevelName(levelName: LevelName) : Path | null {
        const kebabLevelName = levelName.replaceAll(' ', '-');
        const storyPath = '/story/' + kebabLevelName;
        if (isPath(storyPath)) {
            return storyPath;
        }
        return null;
    }
}